<template>
  <div ref="wrapRef" class="tenant-select" :style="props.style">
    <a-select
      v-model:value="tenantId"
      style="width: 100%"
      :options="tenantOptions"
      :allow-clear="allowClear"
      :placeholder="placeholder"
      show-search
      :filter-option="false"
      :loading="loading"
      popup-class-name="tenant-select-dropdown"
      :dropdown-style="{ zIndex: 999 }"
      :get-popup-container="getPopupContainer"
      @select="handleSelect"
      @change="handleChange"
      @search="handleSearch"
      @clear="handleClear"
    ></a-select>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, type CSSProperties } from 'vue'
import { debounce } from 'lodash-es'
import * as tenantAPI from '@/api/tenant'
import type { TenantItem } from '@/api/tenant/types'

defineOptions({
  name: 'tenantSelect',
})

type Props = {
  style?: CSSProperties
  allowClear?: boolean
  placeholder?: string
  hasFull?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  style: () => ({ width: '100%' }),
  allowClear: false,
  placeholder: '请选择',
  hasFull: false,
})

type Option = {
  class: string
  disabled: boolean
  key: string
  title: string
  value: string | null
}

const emit = defineEmits<{
  (e: 'change', value: string | null, option: Option): void
  (e: 'select', value: string | null, option: Option): void
}>()

const tenantId = defineModel<string | null>('tenantId', {
  default: null,
})
type TenantOption = {
  label: string
  value: string | null
}

const wrapRef = ref<HTMLElement | null>(null)

/** 挂到组件容器内，避免下拉层挂到 body 后盖住 sticky 顶栏 */
const getPopupContainer = () => wrapRef.value || document.body

const tenantOptions = ref<TenantOption[]>([])
const loading = ref(false)
const currentRequestId = ref(0)
const selectedOption = ref<TenantOption | null>(null)

const normalizeTenantId = (value: TenantItem['tenantId']) =>
  value === undefined || value === null || value === '' ? null : String(value)

const mapTenantRowsToOptions = (rows: TenantItem[]) =>
  rows.map((item) => ({
    label: item.tenantName || '',
    value: normalizeTenantId(item.tenantId),
  }))

const dedupeOptions = (options: TenantOption[]) => {
  const seen = new Set<string>()
  const result: TenantOption[] = []

  for (const item of options) {
    const key = item.value === null ? '__NULL__' : String(item.value)
    if (seen.has(key)) continue
    seen.add(key)
    result.push(item)
  }

  return result
}

const mergeOptions = (options: TenantOption[]) => {
  const merged: TenantOption[] = []

  if (props.hasFull) {
    merged.push({ label: '全部', value: null })
  }

  if (selectedOption.value) {
    merged.push(selectedOption.value)
  }

  merged.push(...options)
  tenantOptions.value = dedupeOptions(merged)
}

const resolveSelectedOption = (value: string | null) => {
  if (value === null) {
    return props.hasFull ? { label: '全部', value: null } : null
  }

  return tenantOptions.value.find((item) => item.value === value) ?? { label: value, value }
}

const fetchTenantOptions = async (keyword = '') => {
  const requestId = ++currentRequestId.value
  try {
    loading.value = true
    const res = await tenantAPI.queryTenant({
      pageNo: 1,
      pageSize: keyword.trim() ? 20 : 1000,
      tenantName: keyword.trim() || undefined,
    })

    if (requestId !== currentRequestId.value) return

    const { rows } = res.data
    const options = mapTenantRowsToOptions(Array.isArray(rows) ? rows : [])
    mergeOptions(options)

    if (tenantId.value === null) {
      selectedOption.value = props.hasFull ? { label: '全部', value: null } : null
    } else {
      selectedOption.value = resolveSelectedOption(tenantId.value)
    }
  } catch (error) {
    console.error('❌ 获取客户列表失败', error)
  } finally {
    if (requestId === currentRequestId.value) {
      loading.value = false
    }
  }
}

const handleSearch = debounce((value: string) => {
  void fetchTenantOptions(value)
}, 350)

void fetchTenantOptions()

const handleSelect = (value: string | null, option: Option) => {
  if (value === null) {
    selectedOption.value = props.hasFull ? { label: '全部', value: null } : null
  } else {
    selectedOption.value = resolveSelectedOption(value)
  }
  emit('select', value, option)
}

const handleChange = (value: string | null, option: Option) => {
  if (value === null) {
    selectedOption.value = props.hasFull ? { label: '全部', value: null } : null
  } else {
    selectedOption.value = resolveSelectedOption(value)
  }
  emit('change', value, option)
}

const handleClear = () => {
  handleSearch.cancel()
  selectedOption.value = props.hasFull ? { label: '全部', value: null } : null
  void fetchTenantOptions()
}

watch(
  () => tenantId.value,
  (value) => {
    if (value === null) {
      selectedOption.value = props.hasFull ? { label: '全部', value: null } : null
      return
    }
    selectedOption.value = resolveSelectedOption(value)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  handleSearch.cancel()
})
</script>

<style lang="less">
.tenant-select {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  overflow: visible;
}

/* 顶栏 z-index 为 1100，下拉层不得高于顶栏 */
.ant-select-dropdown.tenant-select-dropdown {
  z-index: 999;
}
</style>
